import { useState, useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { UploadBannerMutation, BannerQuery } from "@/shared/graphql";
import { getCroppedImg } from "../getCroppedImg";
import type { Area } from "react-easy-crop";

export interface BannerValue {
  horizontalId?: string | null;
  verticalId?: string | null;
}

export type Orientation = "horizontal" | "vertical";

export function useBannerLogic({
  value = {},
  onChange,
  successMessage,
  maxSize,
  orientation,
  defaultOrientation,
}: {
  value: BannerValue | undefined;
  onChange: (v: BannerValue) => void;
  orientation?: Orientation;
  /** Слот, открытый первым, пока ни одной картинки нет (см. BannerFieldDefinition) */
  defaultOrientation?: Orientation;
  successMessage?: string;
  accept?: string;
  maxSize?: number;
}) {
  const { t } = useTranslation();

  // Активный слот: жёсткая orientation, иначе тот, где уже есть картинка
  // (портрет важнее: он основной для вертикальных карточек), иначе
  // defaultOrientation, иначе 16:9.
  const [activeImageType, setActiveImageType] = useState<Orientation>(
    orientation ??
      (value.verticalId
        ? "vertical"
        : value.horizontalId
          ? "horizontal"
          : (defaultOrientation ?? "horizontal"))
  );

  // Пока поле пустое, смена defaultOrientation (тип тега выбран уже после
  // открытия формы) переключает слот; заполненное поле не трогаем.
  const isFieldEmpty = !value.verticalId && !value.horizontalId;
  useEffect(() => {
    if (!orientation && isFieldEmpty && defaultOrientation) {
      setActiveImageType(defaultOrientation);
    }
  }, [defaultOrientation, isFieldEmpty, orientation]);

  const [hover, setHover] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [horizontalLocalImage, setHorizontalLocalImage] = useState<
    string | null
  >(null);
  const [verticalLocalImage, setVerticalLocalImage] = useState<string | null>(
    null
  );
  const [horizontalCroppedImage, setHorizontalCroppedImage] = useState<
    string | null
  >(null);
  const [verticalCroppedImage, setVerticalCroppedImage] = useState<
    string | null
  >(null);

  const [isCropping, setIsCropping] = useState(false);

  const { data: horizontalBannerData } = useQuery(BannerQuery, {
    variables: { id: value.horizontalId ?? "" },
    skip: !value.horizontalId,
  });
  const { data: verticalBannerData } = useQuery(BannerQuery, {
    variables: { id: value.verticalId ?? "" },
    skip: !value.verticalId,
  });

  const horizontalImage = {
    value: value.horizontalId ?? null,
    previewUrl: horizontalBannerData?.file?.url ?? undefined,
  };
  const verticalImage = {
    value: value.verticalId ?? null,
    previewUrl: verticalBannerData?.file?.url ?? undefined,
  };

  const onHorizontalImageChange = useCallback(
    (id: string | null) => {
      onChange({ ...value, horizontalId: id ?? undefined });
    },
    [onChange, value]
  );
  const onVerticalImageChange = useCallback(
    (id: string | null) => {
      onChange({ ...value, verticalId: id ?? undefined });
    },
    [onChange, value]
  );

  const getCurrentValues = useCallback(() => {
    if (activeImageType === "horizontal") {
      return {
        currentValue: horizontalImage.value,
        currentImage:
          horizontalLocalImage ?? horizontalImage.previewUrl ?? null,
        currentCroppedImage: horizontalCroppedImage,
        aspect: 16 / 9,
        currentOrientation: "horizontal" as Orientation,
      };
    }
    return {
      currentValue: verticalImage.value,
      currentImage: verticalLocalImage ?? verticalImage.previewUrl ?? null,
      currentCroppedImage: verticalCroppedImage,
      aspect: 2 / 3,
      currentOrientation: "vertical" as Orientation,
    };
  }, [
    activeImageType,
    horizontalImage.value,
    verticalImage.value,
    horizontalLocalImage,
    verticalLocalImage,
    horizontalCroppedImage,
    verticalCroppedImage,
    horizontalImage.previewUrl,
    verticalImage.previewUrl,
  ]);

  const {
    currentValue,
    currentImage,
    currentCroppedImage,
    aspect,
    currentOrientation,
  } = getCurrentValues();

  const [mutate, createPreviewResult] = useMutation(UploadBannerMutation, {
    onError(error) {
      enqueueSnackbar(error.message, { variant: "error" });
      if (activeImageType === "horizontal") {
        setHorizontalLocalImage(null);
      } else {
        setVerticalLocalImage(null);
      }
    },
    onCompleted(data) {
      enqueueSnackbar(
        successMessage ??
          t("shared.banner.uploaded", { defaultValue: "Баннер загружен" }),
        { variant: "success" }
      );
      if (activeImageType === "horizontal") {
        onHorizontalImageChange(data.file.id);
      } else {
        onVerticalImageChange(data.file.id);
      }
      setIsCropping(false);
    },
  });

  const createPreview = useCallback(
    (file: File) => {
      return mutate({
        variables: {
          input: { file },
        },
      });
    },
    [mutate]
  );

  const handleFileChange = useCallback(
    (file: File) => {
      if (maxSize && file.size > maxSize) {
        enqueueSnackbar(
          t("shared.banner.tooLarge", {
            defaultValue: "Размер файла превышает {{size}} МБ",
            size: Math.round(maxSize / 1024 / 1024),
          }),
          { variant: "error" }
        );
        return;
      }
      const imageUrl = window.URL.createObjectURL(file);
      if (activeImageType === "horizontal") {
        setHorizontalLocalImage(imageUrl);
      } else {
        setVerticalLocalImage(imageUrl);
      }
      createPreview(file);
    },
    [createPreview, activeImageType, maxSize, t]
  );

  const onDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setHover(true);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setHover(true);
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setHover(false);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setHover(false);
      const file = event.dataTransfer.files[0];
      if (file) handleFileChange(file);
    },
    [handleFileChange]
  );

  const onClickDelete = useCallback(() => {
    setCroppedAreaPixels(null);
    setIsCropping(false);
    if (activeImageType === "horizontal") {
      setHorizontalLocalImage(null);
      setHorizontalCroppedImage(null);
      onHorizontalImageChange(null);
    } else {
      setVerticalLocalImage(null);
      setVerticalCroppedImage(null);
      onVerticalImageChange(null);
    }
  }, [activeImageType, onHorizontalImageChange, onVerticalImageChange]);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onClickCropSave = useCallback(async () => {
    if (!currentImage || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(currentImage, croppedAreaPixels);
      const croppedImageUrl = window.URL.createObjectURL(blob);
      if (activeImageType === "horizontal") {
        setHorizontalCroppedImage(croppedImageUrl);
      } else {
        setVerticalCroppedImage(croppedImageUrl);
      }
      setIsCropping(false);
      const file = new File([blob], "image.jpg", { type: "image/jpeg" });
      await createPreview(file);
    } catch (e) {
      console.error(e);
    }
  }, [createPreview, croppedAreaPixels, currentImage, activeImageType]);

  const onClickCrop = useCallback(() => {
    setIsCropping(true);
  }, []);

  const hasImage = Boolean(currentValue);
  const isEmpty = !currentValue;

  return {
    activeImageType: activeImageType,
    setActiveImageType,
    hover,
    currentImage,
    currentCroppedImage,
    currentOrientation,
    aspect,
    crop,
    zoom,
    createPreviewResult,
    currentValue,
    hasImage,
    isEmpty,
    isCropping,
    horizontalImage,
    verticalImage,
    handleFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClickDelete,
    onCropChange,
    onCropComplete,
    onZoomChange,
    onClickCropSave,
    onClickCrop,
  };
}
